import { Routes } from '@angular/router';
import { ProposalRedirectGuard } from '@app/utils/guards/proposal-redirect.guard';
import { authGuard, UserRole } from '@ess-front/shared';
import { UserTranslationResolver } from '@app/utils/resolvers/user-translation.resolver';
import { TripTranslationResolver } from '@app/utils/resolvers/trip-translation.resolver';
import { DefaultTranslationResolver } from '@app/utils/resolvers/default-translation.resolver';
import { ParamTranslationResolver } from '@app/utils/resolvers/param-translation-resolver.service';
import { UserHashTranslationResolver } from '@app/utils/resolvers/user-hash-translation.resolver';
import { LoginGuard } from '@app/utils/guards/login.guard';
import { DialogType } from '@app/utils/models/dialog-type.model';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'members-area',
    pathMatch: 'full',
  },
  {
    path: 'members-area/login',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  {
    canActivate: [ProposalRedirectGuard],
    path: 'proposal/:hash/destination/:destination',
    loadComponent: () => import('@app/auth/ui/not-found/not-found.component').then(x => x.NotFoundComponent),
  },
  {
    path: 'apply/amex',
    title: 'Amex - Apply Form',
    loadComponent: () =>
      import('@app/forms/ui/apply-form-amex/apply-form-amex.component').then(x => x.ApplyFormAmexComponent),
  },
  {
    path: 'apply/:brand',
    pathMatch: 'full',
    title: 'LATAM - Apply',
    loadComponent: () => import('@app/apply/ui/apply.component').then(x => x.ApplyComponent),
  },
  {
    path: 'apply/:brand/:lang',
    title: 'LATAM - Apply',
    loadComponent: () => import('@app/apply/ui/apply.component').then(x => x.ApplyComponent),
    resolve: {
      language: ParamTranslationResolver,
    },
  },
  {
    path: 'opt-out/amex',
    title: 'Amex - Access Removal',
    loadComponent: () => import('@app/forms/ui/amex-opt-out/amex-opt-out.component').then(x => x.AmexOptOutComponent),
  },
  {
    path: 'apply',
    title: 'Essentialist Apply',
    loadComponent: () => import('@app/apply/ui/apply.component').then(x => x.ApplyComponent),
  },
  {
    path: 'magic-link',
    canActivate: [LoginGuard],
    resolve: {
      language: DefaultTranslationResolver,
    },
    loadComponent: () => import('@app/auth/ui/magic-link/magic-link.component').then(x => x.MagicLinkComponent),
  },
  {
    path: 'payments/:paymentResult',
    loadComponent: () =>
      import('@app/payments/ui/payments-message/payments-messages.component').then(m => m.PaymentsMessagesComponent),
    resolve: {
      language: DefaultTranslationResolver,
    },
  },
  {
    path: 'preferences-form/:hash',
    loadComponent: () =>
      import('@app/forms/ui/preferences-form/preferences-form.component').then(x => x.PreferencesFormComponent),
    resolve: {
      language: UserHashTranslationResolver,
    },
  },
  {
    path: 'preferences-form-completed',
    loadComponent: () =>
      import('@app/forms/ui/preferences-form-completed/preferences-form-completed.component').then(
        x => x.PreferencesFormCompletedComponent,
      ),
  },
  {
    path: 'trip/:hash/confirmation/:id',
    loadChildren: () => import('@app/confirmation/confirmation.module').then(m => m.ConfirmationModule),
    data: {
      type: 'bookingexperience',
    },
    resolve: {
      language: TripTranslationResolver,
    },
  },
  // EXPERIENCE - DEEPLINK - REDIRECTS
  {
    path: 'hotels/:slug',
    redirectTo: 'hotels/:slug/en',
    pathMatch: 'full',
  },
  {
    path: 'experiences/:slug',
    redirectTo: 'experiences/:slug/en',
    pathMatch: 'full',
  },
  {
    path: 'restaurants/:slug',
    redirectTo: 'restaurants/:slug/en',
    pathMatch: 'full',
  },
  {
    path: 'places/:slug',
    redirectTo: 'places/:slug/en',
    pathMatch: 'full',
  },
  // EXPERIENCE - DEEPLINK
  {
    path: 'hotels/:slug/:lang',
    loadChildren: () => import('@app/experience/experience.module').then(m => m.ExperienceModule),
    data: {
      endpoint: 'hotel',
    },
    resolve: {
      language: ParamTranslationResolver,
    },
  },
  {
    path: 'experiences/:slug/:lang',
    loadChildren: () => import('@app/experience/experience.module').then(m => m.ExperienceModule),
    data: {
      endpoint: 'experience',
    },
    resolve: {
      language: ParamTranslationResolver,
    },
  },
  {
    path: 'restaurants/:slug/:lang',
    loadChildren: () => import('@app/experience/experience.module').then(m => m.ExperienceModule),
    data: {
      endpoint: 'restaurant',
    },
    resolve: {
      language: ParamTranslationResolver,
    },
  },
  {
    path: 'places/:slug/:lang',
    loadChildren: () => import('@app/experience/experience.module').then(m => m.ExperienceModule),
    data: {
      endpoint: 'place',
    },
    resolve: {
      language: ParamTranslationResolver,
    },
  },
  //BOOKING EXPERIENCE
  {
    path: 'trip/:hash/experience/:id',
    loadChildren: () => import('@app/experience/experience.module').then(m => m.ExperienceModule),
    data: {
      endpoint: 'bookingexperience',
    },
    resolve: {
      language: TripTranslationResolver,
    },
  },
  // NEW ITINERARY
  {
    path: 'itinerary/:hash',
    loadChildren: () => import('@app/itinerary/ui/itinerary.module').then(m => m.ItineraryModule),
    resolve: {
      language: TripTranslationResolver,
    },
  },
  {
    path: 'trip/:hash',
    loadChildren: () => import('@app/itinerary/ui/itinerary.module').then(m => m.ItineraryModule),
    resolve: {
      language: TripTranslationResolver,
    },
  },
  {
    path: 'sample-trip/:hash',
    loadChildren: () => import('@app/itinerary/ui/itinerary.module').then(m => m.ItineraryModule),
    resolve: {
      language: TripTranslationResolver,
    },
  },
  { path: 'trip-itinerary/:hash', redirectTo: 'itinerary/:hash', pathMatch: 'full' },
  { path: 'proposal/:hash', redirectTo: 'itinerary/:hash', pathMatch: 'full' },

  {
    path: 'itinerary/:hash/checkout',
    loadComponent: () => import('@app/itinerary/ui/checkout/checkout.component').then(c => c.CheckoutComponent),
    resolve: {
      language: TripTranslationResolver,
    },
  },
  {
    path: 'survey/:hash',
    loadChildren: () =>
      import('@app/forms/ui/satisfaction-survey/satisfaction-survey.module').then(m => m.SatisfactionSurveyModule),
    resolve: {
      language: TripTranslationResolver,
    },
  },
  {
    path: 'forbidden',
    loadComponent: () => import('@app/auth/ui/forbidden/forbidden.component').then(x => x.ForbiddenComponent),
  },
  {
    path: 'members-area',
    canActivate: [authGuard(UserRole.MEMBER, 'login')],
    loadComponent: () =>
      import('@app/users-area/ui/members-area/members-area.component').then(m => m.MembersAreaComponent),
    resolve: {
      language: UserTranslationResolver,
    },
    children: [
      {
        path: 'tl/:topLevelTripHash',
        loadComponent: () =>
          import('@app/users-area/ui/dialog-handler/dialog-handler.component').then(mod => mod.DialogHandlerComponent),
        data: { dialogType: DialogType.TopLevelTrip },
      },
    ],
  },
  {
    path: 'staff-area',
    canActivate: [authGuard(UserRole.STAFF, 'login')],
    loadComponent: () => import('@app/users-area/ui/staff-area/staff-area.component').then(m => m.StaffAreaComponent),
    resolve: {
      language: UserTranslationResolver,
    },
    title: 'Essentialist - Staff Area',
  },
  {
    path: 'login',
    canActivate: [LoginGuard],
    loadComponent: () => import('@app/auth/ui/login/login.component').then(x => x.LoginComponent),
    resolve: {
      language: DefaultTranslationResolver,
    },
  },
  {
    path: 'reset-password/:token/:uid',
    loadComponent: () =>
      import('@app/auth/ui/reset-password/reset-password.component').then(x => x.ResetPasswordComponent),
    resolve: {
      language: DefaultTranslationResolver,
    },
  },
  {
    path: 'forgot-password',
    canActivate: [LoginGuard],
    loadComponent: () =>
      import('@app/auth/ui/forgot-password/forgot-password.component').then(x => x.ForgotPasswordComponent),
    resolve: {
      language: DefaultTranslationResolver,
    },
  },
  {
    path: 'authorization-form/:hash',
    loadComponent: () =>
      import('@app/forms/ui/authorization-form/authorization-form.component').then(x => x.AuthorizationFormComponent),
    resolve: {
      language: UserHashTranslationResolver,
    },
  },
  {
    canActivate: [ProposalRedirectGuard],
    path: '**',
    loadComponent: () => import('@app/auth/ui/not-found/not-found.component').then(x => x.NotFoundComponent),
  },
];
